import { createSlice, createAsyncThunk, createSelector, createAction } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { QuizAIService, extractQuizAiInteractionId } from '../utils/quiz-ai-service';
import { addExperienceLocal, updateStreakLocal, updateBadgeProgress, fetchUserGamificationData, } from './gamification-slice';
import { QUESTION_TYPES } from '../constants/subjects';
import { formatError, logError, ErrorCategory } from '../services/errorHandlingService';
import { getUserFacingMessageFromAxiosError, toUserFacingMessage } from '../utils/user-facing-api-error';
import { flattenTemplate, buildQuestionFromBackend, } from './quiz';
const quizAIService = QuizAIService.getInstance();
// Memory optimization constants
const MAX_TEMPLATES_IN_MEMORY = 50;
const MAX_EDITING_QUIZZES = 10;
const MAX_DRAFT_QUIZZES = 15;
const MAX_QUIZ_VERSIONS = 20;
const MEMORY_CLEANUP_INTERVAL = 30000; // 30 seconds
// Helpers to normalize types for service requests
const normalizeRegion = (region) => {
    const allowed = ['UK', 'US', 'UAE', 'Canada', 'Australia', 'International'];
    if (region && allowed.includes(region)) {
        return region;
    }
    return 'UK';
};
const normalizeQuestionType = (qt) => {
    const allowed = Object.values(QUESTION_TYPES);
    if (qt && allowed.includes(qt)) {
        return qt;
    }
    return QUESTION_TYPES.MULTIPLE_CHOICE;
};
// Memory optimization helper functions
const cleanupOldData = (state) => {
    const now = Date.now();
    // Clean up old templates if we have too many
    if (state.templates.length > MAX_TEMPLATES_IN_MEMORY) {
        state.templates = state.templates
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, MAX_TEMPLATES_IN_MEMORY);
    }
    // Clean up old editing quizzes
    const editingKeys = Object.keys(state.editingQuizzes);
    if (editingKeys.length > MAX_EDITING_QUIZZES) {
        const oldestKeys = editingKeys
            .sort((a, b) => {
            const aTime = state.autoSaveStatus[a]?.lastSaved ? new Date(state.autoSaveStatus[a].lastSaved).getTime() : 0;
            const bTime = state.autoSaveStatus[b]?.lastSaved ? new Date(state.autoSaveStatus[b].lastSaved).getTime() : 0;
            return aTime - bTime;
        })
            .slice(0, editingKeys.length - MAX_EDITING_QUIZZES);
        oldestKeys.forEach(key => {
            delete state.editingQuizzes[key];
            delete state.draftQuizzes[key];
            delete state.quizVersions[key];
            delete state.editConflicts[key];
            delete state.editLocks[key];
            delete state.autoSaveStatus[key];
        });
    }
    // Clean up old draft quizzes
    const draftKeys = Object.keys(state.draftQuizzes);
    if (draftKeys.length > MAX_DRAFT_QUIZZES) {
        const oldestKeys = draftKeys
            .sort((a, b) => {
            const aTime = state.autoSaveStatus[a]?.lastSaved ? new Date(state.autoSaveStatus[a].lastSaved).getTime() : 0;
            const bTime = state.autoSaveStatus[b]?.lastSaved ? new Date(state.autoSaveStatus[b].lastSaved).getTime() : 0;
            return aTime - bTime;
        })
            .slice(0, draftKeys.length - MAX_DRAFT_QUIZZES);
        oldestKeys.forEach(key => {
            delete state.draftQuizzes[key];
            delete state.autoSaveStatus[key];
        });
    }
    // Clean up old quiz versions
    Object.keys(state.quizVersions).forEach(quizId => {
        if (state.quizVersions[quizId].length > MAX_QUIZ_VERSIONS) {
            state.quizVersions[quizId] = state.quizVersions[quizId]
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .slice(0, MAX_QUIZ_VERSIONS);
        }
    });
    state.lastMemoryCleanup = now;
};
// Async thunks
export const fetchQuizTemplates = createAsyncThunk('quiz/fetchTemplates', async (params, { rejectWithValue }) => {
    try {
        const tutorId = params && typeof params === 'object' ? params.tutorId : undefined;
        const url = tutorId ? `/quiz-templates?tutor_id=${encodeURIComponent(tutorId)}` : '/quiz-templates';
        const response = await axiosInstance.get(url, {
            timeout: 35000,
        });
        if (!response?.data) {
            return rejectWithValue('No response data');
        }
        if (response.data.success !== true) {
            return rejectWithValue('API returned unsuccessful response');
        }
        const raw = response.data.data;
        const templates = Array.isArray(raw) ? raw : [];
        if (templates.length === 0) {
            return [];
        }
        const flattenedTemplates = templates
            .map((template) => flattenTemplate(template))
            .filter((t) => t != null);
        return flattenedTemplates;
    }
    catch (error) {
        const errorDetails = formatError(error, ErrorCategory.NETWORK, { action: 'fetchQuizTemplates' });
        logError(errorDetails);
        return rejectWithValue(errorDetails.userMessage);
    }
});
export const createQuizTemplate = createAsyncThunk('quiz/createTemplate', async (payload, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/quiz-templates', payload);
        // Check if the response was successful
        if (!response.data.success) {
            return rejectWithValue('API returned unsuccessful response');
        }
        const template = response.data.data;
        const flattened = flattenTemplate(template);
        if (!flattened) {
            return rejectWithValue('Invalid template returned from server');
        }
        return flattened;
    }
    catch (error) {
        const errorDetails = formatError(error, ErrorCategory.DATABASE, { action: 'createQuizTemplate' });
        logError(errorDetails);
        return rejectWithValue(errorDetails.userMessage);
    }
});
export const deleteQuizTemplate = createAsyncThunk('quiz/deleteTemplate', async (templateId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/quiz-templates/${templateId}`);
        if (!response.data.success) {
            return rejectWithValue('API returned unsuccessful response');
        }
        return {
            templateId,
            message: response.data.message || 'Template deleted successfully'
        };
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to delete template' }));
    }
});
export const updateQuizTemplateStatus = createAsyncThunk('quiz/updateTemplateStatus', async ({ templateId, status }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/quiz-templates/${templateId}/status`, { status });
        if (!response.data.success) {
            return rejectWithValue(response.data.message || 'API returned unsuccessful response');
        }
        return { templateId, status };
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update template status' }));
    }
});
// Quiz management actions
export const createQuiz = createAsyncThunk('quiz/createQuiz', async (quizData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/quizzes', quizData);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to create quiz' }));
    }
});
export const updateQuizStatus = createAsyncThunk('quiz/updateQuizStatus', async ({ quizId, status }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/quiz/${quizId}`, { status });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update quiz status' }));
    }
});
export const updateQuiz = createAsyncThunk('quiz/updateQuiz', async ({ quizId, updates }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/quiz/${quizId}`, updates);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update quiz' }));
    }
});
// New API async thunks
export const generateQuestion = createAsyncThunk('quiz/generateQuestion', async (params, { rejectWithValue }) => {
    try {
        const response = await quizAIService.generateQuestion(params);
        if (!response.success) {
            throw new Error(response.error?.message || 'Failed to generate question');
        }
        return response.data;
    }
    catch (error) {
        const errorDetails = formatError(error, ErrorCategory.AI_GENERATION, { action: 'generateQuestion' });
        logError(errorDetails);
        return rejectWithValue(errorDetails.userMessage);
    }
});
export const fetchQuizOptions = createAsyncThunk('quiz/fetchOptions', async (_, { rejectWithValue }) => {
    try {
        return await quizAIService.getOptions();
    }
    catch (error) {
        return rejectWithValue(toUserFacingMessage(error, { fallback: 'Failed to fetch options' }));
    }
});
export const fetchQuizAnalytics = createAsyncThunk('quiz/fetchAnalytics', async (_, { rejectWithValue }) => {
    try {
        return await quizAIService.getAnalytics();
    }
    catch (error) {
        return rejectWithValue(toUserFacingMessage(error, { fallback: 'Failed to fetch analytics' }));
    }
});
// Legacy async thunks for backward compatibility
export const generateQuestionsWithAI = createAsyncThunk('quiz/generateQuestionsWithAI', async (payload, { rejectWithValue }) => {
    const resumeMemory = async () => {
        try {
            const { memoryManager } = await import('../utils/memoryManager');
            memoryManager.resumeMonitoring();
        }
        catch {
            console.warn('Memory manager not available for resume');
        }
    };
    try {
        try {
            const { memoryManager } = await import('../utils/memoryManager');
            memoryManager.pauseMonitoring();
            memoryManager.optimizeForQuizGeneration();
            const memoryStatus = memoryManager.getQuizGenerationMemoryStatus();
            if (!memoryStatus.suitable) {
                console.warn(`⚠️ Memory usage high (${memoryStatus.currentUsage.toFixed(1)}MB): ${memoryStatus.recommendation}`);
            }
        }
        catch {
            console.warn('Memory manager not available, continuing without memory optimization');
        }
        const contextualParams = {
            subtopics: payload.templateData.subtopics || [],
            customPrompt: payload.prompt,
            lessonContent: payload.lessonContent,
            fileContent: payload.file ? await payload.file.text() : undefined,
            learningObjectives: payload.templateData.learningObjectives || [],
            rubric: payload.templateData.rubric,
            classroomContext: {
                subject: payload.templateData.subject || 'Mathematics',
                level: payload.templateData.level || 'intermediate',
                region: payload.templateData.region || 'UK',
            },
        };
        const enhancedPrompt = quizAIService.buildContextualPrompt(contextualParams);
        const td = payload.templateData;
        const explicitSkill = typeof td.targeted_skill === 'string' && td.targeted_skill.trim()
            ? td.targeted_skill.trim().slice(0, 256)
            : null;
        const apiPayload = {
            region: payload.templateData.region || 'UK',
            level: payload.templateData.level || 'intermediate',
            subject: payload.templateData.subject || 'Mathematics',
            subcategory: payload.templateData.subcategory || 'General',
            targeted_skill: explicitSkill ||
                String(payload.templateData.subcategory || 'General').trim().slice(0, 256),
            questionType: payload.templateData.type || 'multiple-choice',
            difficulty: payload.templateData.difficulty,
            prompt: enhancedPrompt,
            lessonContent: payload.lessonContent,
            fileContent: payload.file ? await payload.file.text() : undefined,
            classroomId: payload.templateData.classroom_id || payload.templateData.classroomId,
            rubric_id: payload.templateData.rubric_id,
            rubric: payload.templateData.rubric,
            style_rubric_id: payload.templateData.style_rubric_id,
            qualityThreshold: payload.templateData.qualityThreshold || 0.7,
            validationLevel: payload.templateData.validationLevel || 'basic',
            includeExplanation: payload.templateData.includeExplanation !== false,
            includeReferences: payload.templateData.includeReferences || false,
            autoDistributeQuestionTypes: payload.templateData.autoDistributeQuestionTypes || false,
        };
        const sharedGenerationParams = {
            region: normalizeRegion(apiPayload.region),
            level: apiPayload.level || 'intermediate',
            subject: String(apiPayload.subject),
            subcategory: apiPayload.subcategory || 'General',
            targeted_skill: apiPayload.targeted_skill,
            ...(apiPayload.autoDistributeQuestionTypes
                ? {}
                : { questionType: normalizeQuestionType(apiPayload.questionType) }),
            fileContent: apiPayload.fileContent,
            prompt: apiPayload.prompt,
            lessonContent: apiPayload.lessonContent,
            classroomId: apiPayload.classroomId,
            difficulty: apiPayload.difficulty,
            rubric_id: apiPayload.rubric_id,
            rubric: apiPayload.rubric,
            style_rubric_id: apiPayload.style_rubric_id,
            qualityThreshold: apiPayload.qualityThreshold,
            validationLevel: apiPayload.validationLevel,
            includeExplanation: apiPayload.includeExplanation,
            includeReferences: apiPayload.includeReferences,
        };
        const numberOfQuestions = payload.templateData.numberOfQuestions || 1;
        let result;
        if (numberOfQuestions > 1) {
            const serviceResponse = await quizAIService.generateQuestionsBatch({
                ...sharedGenerationParams,
                options: {
                    autoDistributeQuestionTypes: !!apiPayload.autoDistributeQuestionTypes,
                    batchSize: numberOfQuestions,
                },
            });
            if (!serviceResponse.success) {
                const detail = serviceResponse.error?.details ||
                    serviceResponse.error?.message ||
                    'AI generation failed';
                throw new Error(detail);
            }
            const backendQuestions = Array.isArray(serviceResponse.data)
                ? serviceResponse.data
                : [serviceResponse.data];
            const usableQuestions = apiPayload.style_rubric_id
                ? backendQuestions.filter((q) => q?.metadata?.source !== 'template-fallback')
                : backendQuestions;
            if (usableQuestions.length === 0) {
                throw new Error(apiPayload.style_rubric_id
                    ? 'Board-style generation failed: only template fallbacks were returned. Please retry after confirming quiz AI health.'
                    : 'AI generation failed: no usable questions returned');
            }
            const questions = [];
            usableQuestions.forEach((backendQuestion) => {
                questions.push(buildQuestionFromBackend(backendQuestion));
            });
            result = {
                questions,
                aiInteractionId: extractQuizAiInteractionId(serviceResponse),
            };
        }
        else {
            const serviceResponse = await quizAIService.generateQuestion({
                ...sharedGenerationParams,
                options: { autoDistributeQuestionTypes: !!apiPayload.autoDistributeQuestionTypes },
            });
            if (!serviceResponse.success) {
                const detail = serviceResponse.error?.details ||
                    serviceResponse.error?.message ||
                    'AI generation failed';
                throw new Error(detail);
            }
            if (apiPayload.style_rubric_id &&
                serviceResponse.data?.metadata
                    ?.source === 'template-fallback') {
                throw new Error('Board-style generation failed: template fallback was returned. Please retry after confirming quiz AI health.');
            }
            result = {
                questions: [buildQuestionFromBackend(serviceResponse.data)],
                aiInteractionId: extractQuizAiInteractionId(serviceResponse),
            };
        }
        await resumeMemory();
        return result;
    }
    catch (error) {
        const errorDetails = formatError(error, ErrorCategory.AI_GENERATION, {
            action: 'generateQuestionsWithAI',
        });
        logError(errorDetails);
        await resumeMemory();
        return rejectWithValue(errorDetails.userMessage);
    }
});
export const createAIGeneratedQuizTemplate = createAsyncThunk('quiz/createAIGeneratedQuizTemplate', async (payload, { dispatch, rejectWithValue }) => {
    try {
        // First generate questions
        const { questions, aiInteractionId } = await dispatch(generateQuestionsWithAI(payload)).unwrap();
        // Determine the primary question type from generated questions
        // Count question types to find the most common one
        const questionTypeCounts = questions.reduce((acc, q) => {
            acc[q.type] = (acc[q.type] || 0) + 1;
            return acc;
        }, {});
        // Get the most common question type as the primary type
        const primaryQuestionType = Object.entries(questionTypeCounts)
            .sort(([, a], [, b]) => b - a)[0]?.[0] || payload.templateData.type || 'multiple_choice';
        // Then create template
        const templateData = {
            title: payload.templateData.title,
            description: payload.templateData.description,
            type: primaryQuestionType, // Use the most common question type from generated questions
            difficulty: payload.templateData.difficulty,
            subject_id: payload.templateData.subject_id,
            education_system_id: payload.templateData.education_system_id, // Include education system for inheritance
            academic_level_id: payload.templateData.academic_level_id, // Include academic level for inheritance
            classroom_id: payload.templateData.classroom_id || payload.templateData.classroomId, // Include classroom for inheritance
        };
        const response = await axiosInstance.post('/quiz-templates', {
            ...templateData,
            content: {
                title: templateData.title,
                description: templateData.description,
                questions,
            },
            metadata: {
                isAIGenerated: true,
                generationMethod: payload.method,
                subtopics: payload.templateData.subtopics,
                generatedAt: new Date().toISOString(),
                questionCount: questions.length,
                questionTypeDistribution: questionTypeCounts, // Track the distribution of question types
                primaryQuestionType: primaryQuestionType, // Track the primary question type used
                autoDistributeQuestionTypes: true, // Indicate that this template uses auto distribution
                // Rubric information for scoring guidance (stored in metadata for later use when quiz is created)
                rubric_id: payload.templateData.rubric_id || undefined,
                rubric_name: payload.templateData.rubric?.name || undefined,
                rubric_description: payload.templateData.rubric?.description || undefined,
                // Board-style generation rubric used for this template
                style_rubric_id: payload.templateData.style_rubric_id || undefined,
                ...(aiInteractionId ? { ai_interaction_id: aiInteractionId } : {}),
            },
        });
        const flattened = flattenTemplate(response.data);
        if (!flattened) {
            return rejectWithValue('Invalid template returned from server');
        }
        return flattened;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to create AI-generated template' }));
    }
});
export const checkAIWorkerHealth = createAsyncThunk('quiz/checkAIWorkerHealth', async (_, { rejectWithValue }) => {
    try {
        // Let the health check handle its own timeout - don't race with external timeout
        const result = await quizAIService.checkWorkerHealth();
        // If health check fails, don't throw - return false gracefully
        if (!result) {
            console.warn('🏥 Worker health check returned false - worker may be unavailable');
            return false;
        }
        return result;
    }
    catch (error) {
        console.warn('🏥 Worker health check failed:', error.message);
        // Don't reject - return false instead to prevent Redux errors
        return false;
    }
});
export const generateVisualContent = createAsyncThunk('quiz/generateVisualContent', async (params, { rejectWithValue }) => {
    try {
        return await quizAIService.generateVisualContent(params.content, {
            type: params.type,
            style: params.style,
            subject: params.subject,
            level: params.level,
        });
    }
    catch (error) {
        return rejectWithValue(toUserFacingMessage(error, { fallback: 'Visual content generation failed' }));
    }
});
export const generateEnhancedQuestion = createAsyncThunk('quiz/generateEnhancedQuestion', async (params, { rejectWithValue }) => {
    try {
        const request = {
            questionCount: params.questionCount,
            difficulty: params.difficulty,
            questionType: params.questionType,
            subject: params.subject,
            subtopics: params.subtopics,
            qualityThreshold: params.qualityThreshold,
            validationLevel: params.validationLevel,
            includeExplanation: params.includeExplanation,
            includeReferences: params.includeReferences,
        };
        if (params.method === 'file' && params.file) {
            request.file = params.file;
        }
        else if (params.method === 'prompt' && params.prompt) {
            request.prompt = params.prompt;
        }
        else if (params.method === 'lesson' && params.lessonContent) {
            request.lessonContent = params.lessonContent;
        }
        const response = await quizAIService.generateEnhancedQuestion(request);
        return { questions: response.questions };
    }
    catch (error) {
        return rejectWithValue(toUserFacingMessage(error, { fallback: 'Enhanced question generation failed' }));
    }
});
// Submit quiz completion with gamification
export const submitQuizCompletion = createAsyncThunk('quiz/submitCompletion', async ({ quizId, userId, answers, score, timeSpent }, { dispatch, rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/quiz/submit', {
            quiz_id: quizId,
            user_id: userId,
            answers,
            score,
            time_spent: timeSpent
        });
        const basePoints = 10;
        const scoreMultiplier = score / 100;
        const timeBonus = timeSpent < 300 ? 5 : 0;
        const experiencePoints = Math.floor(basePoints * scoreMultiplier) + timeBonus;
        dispatch(addExperienceLocal(experiencePoints));
        dispatch(updateStreakLocal({ streakType: 'quiz_completion', increment: true }));
        dispatch(updateBadgeProgress({ badgeId: 'quiz_master', progress: 1 }));
        if (score === 100) {
            dispatch(updateBadgeProgress({ badgeId: 'perfect_score', progress: 1 }));
        }
        return {
            experienceEarned: experiencePoints,
            completionDate: new Date().toISOString(),
            score
        };
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to submit quiz' }));
    }
});
// New async thunks for quiz editing
export const saveQuizDraft = createAsyncThunk('quiz/saveDraft', async ({ quizId, draftContent, settings, metadata }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz/${quizId}/save-draft`, {
            draft_content: draftContent,
            settings,
            metadata
        });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to save draft' }));
    }
});
export const updateQuizContent = createAsyncThunk('quiz/updateContent', async ({ quizId, content, settings, metadata }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/quiz/${quizId}`, {
            content,
            settings,
            metadata
        });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update content' }));
    }
});
export const publishQuiz = createAsyncThunk('quiz/publish', async (quizId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz/${quizId}/publish`);
        if (response.data?.success === false) {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to publish quiz' }));
        }
        if (response.data?.data == null) {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Publish returned no data' }));
        }
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to publish quiz' }));
    }
});
export const getQuizVersionHistory = createAsyncThunk('quiz/getVersionHistory', async (quizId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/quiz/${quizId}/versions`);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to get version history' }));
    }
});
export const acquireEditLock = createAsyncThunk('quiz/acquireEditLock', async ({ quizId, tutorId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz/${quizId}/lock`, { tutor_id: tutorId });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to acquire edit lock' }));
    }
});
export const releaseEditLock = createAsyncThunk('quiz/releaseEditLock', async ({ quizId, tutorId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/quiz/${quizId}/lock`, {
            data: { tutor_id: tutorId }
        });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to release edit lock' }));
    }
});
export const autoSaveQuiz = createAsyncThunk('quiz/autoSave', async ({ quizId, draftContent, metadata }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz/${quizId}/auto-save`, {
            draft_content: draftContent,
            metadata
        });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to auto-save quiz' }));
    }
});
/** Resolved quiz for QuizTakingInterface — must be declared before quizSlice extraReducers. */
export const fetchQuizById = createAsyncThunk('quiz/fetchQuizById', async (quizId) => {
    const response = await axiosInstance.get(`/quiz/${quizId}`);
    return response.data;
});
export const clearQuizState = createAction('quiz/clearQuizState');
export const submitQuiz = createAsyncThunk('quiz/submitQuiz', async (quizData, { rejectWithValue, dispatch, getState }) => {
    try {
        const response = await axiosInstance.post('/quiz-submissions/submit', {
            quiz_id: quizData.quiz_id,
            answers: quizData.answers,
            ...(quizData.study_session_id ? { study_session_id: quizData.study_session_id } : {}),
            ...(quizData.time_spent != null ? { time_spent: quizData.time_spent } : {}),
            ...(quizData.attempt_telemetry ? { attempt_telemetry: quizData.attempt_telemetry } : {}),
            ...(quizData.meeting_id ? { meeting_id: quizData.meeting_id } : {}),
        });
        const uid = getState().auth?.user?.id;
        if (uid) {
            dispatch(fetchUserGamificationData(undefined));
        }
        return response.data;
    }
    catch (error) {
        const status = error?.response?.status;
        const serverMsg = error?.response?.data?.message;
        if (status === 409) {
            return rejectWithValue(toUserFacingMessage(serverMsg, { fallback: 'You have already completed this quiz.' }));
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to submit quiz' }));
    }
});
const initialState = {
    templates: [],
    activeTemplateId: null,
    activeQuiz: null,
    status: 'idle',
    error: null,
    aiGenerationStatus: 'idle',
    aiGenerationError: null,
    generatedQuestions: [],
    lastQuizAiInteractionId: null,
    aiWorkerHealth: null,
    currentQuestion: null,
    quizOptions: null,
    analytics: null,
    completedQuizzes: [],
    quizScores: {},
    streakCount: 0,
    lastCompletionDate: null,
    submissionStatus: 'idle',
    submissionError: null,
    lastSubmissionResult: null,
    is_loading: false,
    // Enhanced editing state
    editingQuizzes: {},
    draftQuizzes: {},
    quizVersions: {},
    editConflicts: {},
    editLocks: {},
    autoSaveStatus: {},
    // Memory management state
    lastMemoryCleanup: 0,
    memoryOptimizationEnabled: true,
};
const quizSlice = createSlice({
    name: 'quiz',
    initialState,
    reducers: {
        setActiveTemplateId: (state, action) => {
            state.activeTemplateId = action.payload;
        },
        updateTemplateField: (state, action) => {
            const template = state.templates.find(t => t.id === action.payload.templateId);
            if (template) {
                Object.assign(template, action.payload.updates);
            }
        },
        updateQuestion: (state, action) => {
            const template = state.templates.find(t => t.id === action.payload.templateId);
            if (template) {
                const question = template.questions.find(q => q.id === action.payload.questionId);
                if (question) {
                    Object.assign(question, action.payload.updates);
                }
            }
        },
        clearGeneratedQuestions: (state) => {
            state.generatedQuestions = [];
            state.aiGenerationStatus = 'idle';
            state.aiGenerationError = null;
            state.lastQuizAiInteractionId = null;
        },
        clearAIGenerationError: (state) => {
            state.aiGenerationError = null;
        },
        addGeneratedQuestionToTemplate: (state, action) => {
            const template = state.templates.find(t => t.id === action.payload.templateId);
            if (template) {
                const questionWithPoints = {
                    ...action.payload.question,
                    points: action.payload.question.points || 1
                };
                template.questions.push(questionWithPoints);
            }
        },
        recordQuizCompletion: (state, action) => {
            const { quizId, score } = action.payload;
            if (!state.completedQuizzes.includes(quizId)) {
                state.completedQuizzes.push(quizId);
            }
            state.quizScores[quizId] = score;
            state.lastCompletionDate = new Date().toISOString();
        },
        clearCurrentQuestion: (state) => {
            state.currentQuestion = null;
        },
        // New editing reducers
        setEditingQuiz: (state, action) => {
            state.editingQuizzes[action.payload.quizId] = action.payload.data;
        },
        clearEditingQuiz: (state, action) => {
            delete state.editingQuizzes[action.payload];
        },
        setDraftQuiz: (state, action) => {
            state.draftQuizzes[action.payload.quizId] = action.payload.data;
        },
        clearDraftQuiz: (state, action) => {
            delete state.draftQuizzes[action.payload];
        },
        setQuizVersions: (state, action) => {
            state.quizVersions[action.payload.quizId] = action.payload.versions;
        },
        setEditLock: (state, action) => {
            state.editLocks[action.payload.quizId] = action.payload.lockData;
        },
        clearEditLock: (state, action) => {
            delete state.editLocks[action.payload];
        },
        setAutoSaveStatus: (state, action) => {
            state.autoSaveStatus[action.payload.quizId] = action.payload.status;
        },
        clearEditConflicts: (state, action) => {
            delete state.editConflicts[action.payload];
        },
        // Memory optimization actions
        enableMemoryOptimization: (state) => {
            state.memoryOptimizationEnabled = true;
        },
        disableMemoryOptimization: (state) => {
            state.memoryOptimizationEnabled = false;
        },
        forceMemoryCleanup: (state) => {
            cleanupOldData(state);
        },
    },
    extraReducers: (builder) => {
        builder
            // Template operations
            .addCase(fetchQuizTemplates.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(fetchQuizTemplates.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.templates = Array.isArray(action.payload) ? action.payload : [];
            state.error = null;
            // Memory cleanup after successful template fetch
            if (state.memoryOptimizationEnabled && Date.now() - state.lastMemoryCleanup > MEMORY_CLEANUP_INTERVAL) {
                cleanupOldData(state);
            }
        })
            .addCase(fetchQuizTemplates.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred fetching templates';
        })
            .addCase(createQuizTemplate.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(createQuizTemplate.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.templates.push(action.payload);
        })
            .addCase(createQuizTemplate.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred creating template';
        })
            .addCase(deleteQuizTemplate.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(deleteQuizTemplate.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.templates = state.templates.filter(template => template.id !== action.payload.templateId);
        })
            .addCase(deleteQuizTemplate.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred deleting template';
        })
            .addCase(updateQuizTemplateStatus.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const t = state.templates.find(template => template.id === action.payload.templateId);
            if (t && 'status' in t)
                t.status = action.payload.status;
        })
            .addCase(updateQuizTemplateStatus.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred updating template status';
        })
            // Quiz management operations
            .addCase(createQuiz.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(createQuiz.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Note: We don't store quizzes in the template state, they're managed separately
        })
            .addCase(createQuiz.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred creating quiz';
        })
            .addCase(updateQuizStatus.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(updateQuizStatus.fulfilled, (state, action) => {
            state.status = 'succeeded';
            // Update the quiz in editing state if it exists
            const quizId = action.payload.id;
            if (state.editingQuizzes[quizId]) {
                state.editingQuizzes[quizId] = { ...state.editingQuizzes[quizId], ...action.payload };
            }
        })
            .addCase(updateQuizStatus.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred updating quiz status';
        })
            // New editing operations
            .addCase(saveQuizDraft.pending, (state, action) => {
            const quizId = action.meta.arg.quizId;
            state.autoSaveStatus[quizId] = { lastSaved: null, isSaving: true };
        })
            .addCase(saveQuizDraft.fulfilled, (state, action) => {
            const quizId = action.payload.id;
            state.draftQuizzes[quizId] = action.payload;
            state.autoSaveStatus[quizId] = {
                lastSaved: new Date().toISOString(),
                isSaving: false
            };
            // Clear any edit conflicts
            delete state.editConflicts[quizId];
        })
            .addCase(saveQuizDraft.rejected, (state, action) => {
            const quizId = action.meta.arg.quizId;
            state.autoSaveStatus[quizId] = { lastSaved: null, isSaving: false };
            state.error = action.payload || 'Failed to save draft';
        })
            .addCase(updateQuizContent.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(updateQuizContent.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const quizId = action.payload.id;
            if (state.editingQuizzes[quizId]) {
                state.editingQuizzes[quizId] = { ...state.editingQuizzes[quizId], ...action.payload };
            }
            // Clear any edit conflicts
            delete state.editConflicts[quizId];
        })
            .addCase(updateQuizContent.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Failed to update quiz content';
        })
            .addCase(publishQuiz.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        })
            .addCase(publishQuiz.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const payload = action.payload;
            const quizId = payload.id;
            const templateId = payload.template_id ?? action.meta.arg;
            const passedId = action.meta.arg;
            // Update template in templates array so UI shows published status
            const templateToUpdate = state.templates.find((t) => t.id === templateId || t.id === passedId || t.id === quizId);
            if (templateToUpdate) {
                templateToUpdate.status = 'published';
            }
            // Clean up editing/draft state - use both quiz id and passed id (template_id)
            // since frontend may key by either when publishing from AI generator
            for (const key of [quizId, passedId, templateId].filter(Boolean)) {
                delete state.editingQuizzes[key];
                delete state.draftQuizzes[key];
                delete state.editLocks[key];
            }
        })
            .addCase(publishQuiz.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Failed to publish quiz';
        })
            .addCase(getQuizVersionHistory.fulfilled, (state, action) => {
            const quizId = action.meta.arg;
            state.quizVersions[quizId] = action.payload;
        })
            .addCase(acquireEditLock.fulfilled, (state, action) => {
            const quizId = action.meta.arg.quizId;
            if (action.payload.locked) {
                state.editLocks[quizId] = action.payload;
            }
        })
            .addCase(releaseEditLock.fulfilled, (state, action) => {
            const quizId = action.meta.arg.quizId;
            if (action.payload.released) {
                delete state.editLocks[quizId];
            }
        })
            .addCase(autoSaveQuiz.fulfilled, (state, action) => {
            const quizId = action.meta.arg.quizId;
            state.autoSaveStatus[quizId] = {
                lastSaved: new Date().toISOString(),
                isSaving: false
            };
        })
            // AI Generation operations
            .addCase(generateQuestion.pending, (state) => {
            state.aiGenerationStatus = 'generating';
            state.aiGenerationError = null;
        })
            .addCase(generateQuestion.fulfilled, (state, action) => {
            state.aiGenerationStatus = 'succeeded';
            state.currentQuestion = action.payload;
        })
            .addCase(generateQuestion.rejected, (state, action) => {
            state.aiGenerationStatus = 'failed';
            state.aiGenerationError = action.payload || 'Question generation failed';
        })
            .addCase(generateEnhancedQuestion.pending, (state) => {
            state.aiGenerationStatus = 'generating';
            state.aiGenerationError = null;
            state.generatedQuestions = [];
        })
            .addCase(generateEnhancedQuestion.fulfilled, (state, action) => {
            state.aiGenerationStatus = 'succeeded';
            state.generatedQuestions = action.payload.questions;
            // Memory cleanup after successful generation
            if (state.memoryOptimizationEnabled && Date.now() - state.lastMemoryCleanup > MEMORY_CLEANUP_INTERVAL) {
                cleanupOldData(state);
            }
        })
            .addCase(generateEnhancedQuestion.rejected, (state, action) => {
            state.aiGenerationStatus = 'failed';
            state.aiGenerationError = action.payload || 'Enhanced question generation failed';
        })
            // Batch AI Generation operations
            .addCase(generateQuestionsWithAI.pending, (state) => {
            state.aiGenerationStatus = 'generating';
            state.aiGenerationError = null;
            state.generatedQuestions = [];
            state.lastQuizAiInteractionId = null;
        })
            .addCase(generateQuestionsWithAI.fulfilled, (state, action) => {
            state.aiGenerationStatus = 'succeeded';
            state.generatedQuestions = action.payload.questions;
            state.lastQuizAiInteractionId = action.payload.aiInteractionId ?? null;
            // Memory cleanup after successful generation
            if (state.memoryOptimizationEnabled && Date.now() - state.lastMemoryCleanup > MEMORY_CLEANUP_INTERVAL) {
                cleanupOldData(state);
            }
        })
            .addCase(generateQuestionsWithAI.rejected, (state, action) => {
            state.aiGenerationStatus = 'failed';
            state.aiGenerationError = action.payload || 'Batch question generation failed';
        })
            // Quiz completion with gamification
            .addCase(submitQuizCompletion.fulfilled, (state, action) => {
            const { score } = action.payload;
            // This will be handled by the recordQuizCompletion action
        })
            // Quiz submission (QuizTakingInterface)
            .addCase(submitQuiz.pending, (state) => {
            state.submissionStatus = 'submitting';
            state.submissionError = null;
        })
            .addCase(submitQuiz.fulfilled, (state, action) => {
            state.submissionStatus = 'succeeded';
            state.submissionError = null;
            const data = action.payload?.data;
            if (data) {
                state.lastSubmissionResult = {
                    submission: data.submission,
                    score: data.score,
                    objectiveScore: data.objectiveScore ?? data.score,
                    rubricOverallPercent: data.rubricOverallPercent ?? null,
                    ...(data.compositeScore != null ? { compositeScore: data.compositeScore } : {}),
                    totalQuestions: data.totalQuestions,
                    correctAnswers: data.correctAnswers,
                    feedback: data.feedback,
                    ...(data.rubricScore ? { rubricScore: data.rubricScore } : {})
                };
            }
        })
            .addCase(submitQuiz.rejected, (state, action) => {
            state.submissionStatus = 'failed';
            state.submissionError = action.payload || 'Failed to submit quiz';
        })
            // AI Worker Health Check
            .addCase(checkAIWorkerHealth.pending, (state) => {
            state.aiWorkerHealth = null;
        })
            .addCase(checkAIWorkerHealth.fulfilled, (state, action) => {
            state.aiWorkerHealth = action.payload;
        })
            .addCase(checkAIWorkerHealth.rejected, (state, action) => {
            state.aiWorkerHealth = false;
            state.error = action.payload || 'Worker health check failed';
        })
            .addCase(fetchQuizById.pending, (state) => {
            state.activeQuiz = null;
            state.is_loading = true;
            state.submissionStatus = 'idle';
            state.submissionError = null;
            state.lastSubmissionResult = null;
        })
            .addCase(fetchQuizById.fulfilled, (state, action) => {
            const p = action.payload;
            const quiz = p && typeof p === 'object' && p !== null && 'data' in p && p.data !== undefined
                ? p.data
                : action.payload;
            state.activeQuiz = quiz;
            state.is_loading = false;
        })
            .addCase(fetchQuizById.rejected, (state) => {
            state.activeQuiz = null;
            state.is_loading = false;
        })
            .addCase(clearQuizState, (state) => {
            state.activeQuiz = null;
            state.is_loading = false;
            state.submissionStatus = 'idle';
            state.submissionError = null;
            state.lastSubmissionResult = null;
        });
    },
});
// Export actions
export const { setActiveTemplateId, updateTemplateField, updateQuestion, clearGeneratedQuestions, clearAIGenerationError, addGeneratedQuestionToTemplate, recordQuizCompletion, clearCurrentQuestion, 
// New editing actions
setEditingQuiz, clearEditingQuiz, setDraftQuiz, clearDraftQuiz, setQuizVersions, setEditLock, clearEditLock, setAutoSaveStatus, clearEditConflicts, 
// Memory optimization actions
enableMemoryOptimization, disableMemoryOptimization, forceMemoryCleanup, } = quizSlice.actions;
// Selectors
export const selectActiveTemplate = (state) => {
    const { templates, activeTemplateId } = state.quiz;
    if (!activeTemplateId)
        return null;
    return templates.find(t => t.id === activeTemplateId) || null;
};
export const selectLastQuizAiInteractionId = (state) => state.quiz.lastQuizAiInteractionId;
export const selectAIGenerationStatus = (state) => state.quiz.aiGenerationStatus;
export const selectGeneratedQuestions = (state) => state.quiz.generatedQuestions;
export const selectAIGenerationError = (state) => state.quiz.aiGenerationError;
export const selectAIWorkerHealth = (state) => state.quiz.aiWorkerHealth;
export const selectCurrentQuestion = (state) => state.quiz.currentQuestion;
export const selectQuizOptions = (state) => state.quiz.quizOptions;
export const selectQuizAnalytics = (state) => state.quiz.analytics;
export const selectCompletedQuizzes = (state) => state.quiz.completedQuizzes;
export const selectQuizScores = (state) => state.quiz.quizScores;
export const selectQuizStreakCount = (state) => state.quiz.streakCount;
export const selectLastCompletionDate = (state) => state.quiz.lastCompletionDate;
// New editing selectors
export const selectEditingQuiz = (state, quizId) => state.quiz.editingQuizzes[quizId];
export const selectDraftQuiz = (state, quizId) => state.quiz.draftQuizzes[quizId];
export const selectQuizVersions = (state, quizId) => state.quiz.quizVersions[quizId] || [];
export const selectEditLock = (state, quizId) => state.quiz.editLocks[quizId];
export const selectAutoSaveStatus = (state, quizId) => state.quiz.autoSaveStatus[quizId] || { lastSaved: null, isSaving: false };
export const selectEditConflicts = (state, quizId) => state.quiz.editConflicts[quizId];
// Memory optimization selectors
export const selectMemoryOptimizationEnabled = (state) => state.quiz.memoryOptimizationEnabled;
export const selectLastMemoryCleanup = (state) => state.quiz.lastMemoryCleanup;
// Memoized selectors for performance optimization
export const selectTemplatesMemoized = createSelector([(state) => state.quiz.templates], (templates) => templates);
export const selectActiveTemplateMemoized = createSelector([(state) => state.quiz.templates, (state) => state.quiz.activeTemplateId], (templates, activeTemplateId) => activeTemplateId ? templates.find(t => t.id === activeTemplateId) : null);
export const selectTemplatesByTypeMemoized = createSelector([(state) => state.quiz.templates, (state, type) => type], (templates, type) => templates.filter(t => t.type === type));
export const selectTemplatesByDifficultyMemoized = createSelector([(state) => state.quiz.templates, (state, difficulty) => difficulty], (templates, difficulty) => templates.filter(t => t.difficulty === difficulty));
export const selectTemplatesCountMemoized = createSelector([(state) => state.quiz.templates], (templates) => templates.length);
export const selectQuizStatusMemoized = createSelector([(state) => state.quiz.status, (state) => state.quiz.error], (status, error) => ({ status, error }));
export default quizSlice.reducer;
